import { MAX_PRINT_METHOD_NAME_LENGTH } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import type { RenameController } from '@/shared/lib';
import { Button, cn, InlineRenameForm, tableStyles } from '@/shared/ui';
import { PrintMethodDescriptionForm } from './print-method-description-form';

interface PrintMethodRowMethod {
  readonly id: string;
  readonly name: string;
  /** 고객 프린트 가이드 설명 — 없으면 null. */
  readonly description: string | null;
  /** 적용 상품 수 — 미사용 방식을 정리할 때의 판단 근거. */
  readonly productCount: number;
}

/** 설명 편집 상태 — 편집 중인 행의 id 와 입력 중인 값. */
export interface DescriptionController {
  readonly target: {
    readonly id: string;
    readonly value: string;
    readonly isPending: boolean;
  } | null;
  readonly onChange: (value: string) => void;
  readonly onSubmit: () => void;
  readonly onCancel: () => void;
}

interface PrintMethodRowProps {
  readonly method: PrintMethodRowMethod;
  readonly rename: RenameController;
  readonly description: DescriptionController;
  readonly onRenameStart: (method: PrintMethodRowMethod) => void;
  readonly onDescriptionStart: (method: PrintMethodRowMethod) => void;
  readonly onDelete: (method: PrintMethodRowMethod) => void;
}

/** 인쇄 방식 표의 한 행 — 이름(인라인 수정)·설명(인라인 수정)·적용 상품 수·삭제. */
export const PrintMethodRow = ({
  method,
  rename,
  description,
  onRenameStart,
  onDescriptionStart,
  onDelete,
}: PrintMethodRowProps): ReactElement => {
  const isRenaming = rename.target?.id === method.id;
  const isEditingDescription = description.target?.id === method.id;

  return (
    <tr className={tableStyles.row}>
      <td className={cn(tableStyles.td, 'font-medium')}>
        {isRenaming && rename.target ? (
          <InlineRenameForm
            ariaLabel={`${method.name} 새 이름`}
            name={rename.target.name}
            maxLength={MAX_PRINT_METHOD_NAME_LENGTH}
            isPending={rename.target.isPending}
            onNameChange={rename.onChange}
            onSubmit={rename.onSubmit}
            onCancel={rename.onCancel}
          />
        ) : (
          method.name
        )}
      </td>
      <td className={cn(tableStyles.td, 'align-top')}>
        {isEditingDescription && description.target ? (
          <PrintMethodDescriptionForm
            ariaLabel={`${method.name} 설명`}
            value={description.target.value}
            isPending={description.target.isPending}
            onChange={description.onChange}
            onSubmit={description.onSubmit}
            onCancel={description.onCancel}
          />
        ) : (
          <span className="flex items-start justify-between gap-2">
            <span
              className={
                method.description === null ? 'text-muted-foreground' : 'whitespace-pre-line'
              }
            >
              {method.description ?? '설명 없음'}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="shrink-0"
              aria-label={`${method.name} 설명 수정`}
              onClick={() => onDescriptionStart(method)}
            >
              설명 수정
            </Button>
          </span>
        )}
      </td>
      <td className={cn(tableStyles.td, 'text-muted-foreground')}>
        {method.productCount.toLocaleString('ko-KR')}개
      </td>
      <td className={tableStyles.td}>
        <span className="flex items-center gap-1">
          {isRenaming ? null : (
            <Button variant="ghost" size="sm" onClick={() => onRenameStart(method)}>
              수정
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive"
            disabled={method.productCount > 0}
            aria-label={`${method.name} 삭제`}
            onClick={() => onDelete(method)}
          >
            삭제
          </Button>
        </span>
      </td>
    </tr>
  );
};
